import {
  AllResultTypes,
  bulkDropdownEditor,
  bulkInningEditor,
  bulkNumberStepperEditor,
  bulkPlayerEditor,
  CallTypes,
  Dexterity,
  dropdownRenderer,
  enumToDropdownValuesMap,
  HitTypes,
  IBulkEditorOptions,
  IBulkEditorParams,
  ICellRendererParams,
  IDataGridColumnConfiguration,
  inningRenderer,
  KOrBB,
  measurementRenderer,
  numberStepperRenderer,
  PitchTypes,
  playerRenderer,
  ResultTypes,
  RightLeft,
  simpleHeaderRenderer,
  valueFormatters,
} from '@trackman/baseball-shared-components';
import { absChallengeRenderer } from 'components/ABSChallenge/ABSChallengeRenderer';
import { ColumnName } from 'enums/ColumnName';
import { DataKey } from 'enums/DataKey';
import { IReviewDataRow } from 'interfaces/review/IReviewDataRow';
import React from 'react';
import { IReviewInning } from '../../../interfaces/review/IReviewInning';
import { ReviewDataRow } from '../../../models/review/ReviewDataRow/ReviewDataRow';
import defensivePlayerRenderer from './Renderers/DefensivePlayerRenderer';
import noteRenderer from './Renderers/NoteRenderer';
import { timeCellRenderer } from './Renderers/TimeCellRenderer';
import baseRunnerRenderer from './Renderers/baseRunnerRenderer';

const noteColumn = {
  dataKey: DataKey.Note,
  columnName: ColumnName.Note,
  cellRenderer: noteRenderer,
  headerRenderer: simpleHeaderRenderer,
  width: 200,
} as IDataGridColumnConfiguration;

const pitchNoColumn = {
  dataKey: DataKey.PitchNo,
  columnName: ColumnName.PitchNo,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) => {
    return (
      <span style={{ marginLeft: 15 }}>
        {data.value}
        {data.row.isChanged ? '*' : ''}
      </span>
    );
  },
  headerRenderer: simpleHeaderRenderer,
  width: 100,
} as IDataGridColumnConfiguration;

const pitcherColumn = {
  dataKey: DataKey.Pitcher,
  columnName: ColumnName.Pitcher,
  cellRenderer: playerRenderer,
  headerRenderer: simpleHeaderRenderer,
  bulkEditorOptions: {
    renderer: bulkPlayerEditor,
  },
  width: 180,
} as IDataGridColumnConfiguration;

const catcherColumn = {
  dataKey: DataKey.Catcher,
  columnName: ColumnName.Catcher,
  cellRenderer: playerRenderer,
  headerRenderer: simpleHeaderRenderer,
  bulkEditorOptions: {
    renderer: bulkPlayerEditor,
  },
  width: 180,
} as IDataGridColumnConfiguration;

const batterColumn = {
  dataKey: DataKey.Batter,
  columnName: ColumnName.Batter,
  cellRenderer: playerRenderer,
  headerRenderer: simpleHeaderRenderer,
  bulkEditorOptions: {
    renderer: bulkPlayerEditor,
  },
  width: 180,
} as IDataGridColumnConfiguration;

const ballsColumn = {
  dataKey: DataKey.Balls,
  columnName: ColumnName.Balls,
  cellRenderer: numberStepperRenderer,
  headerRenderer: simpleHeaderRenderer,
  bulkEditorOptions: {
    renderer: bulkNumberStepperEditor,
    defaultValue: 0,
  } as IBulkEditorOptions<number>,
  width: 150,
} as IDataGridColumnConfiguration;

const strikesColumn = {
  dataKey: DataKey.Strikes,
  columnName: ColumnName.Strikes,
  cellRenderer: numberStepperRenderer,
  headerRenderer: simpleHeaderRenderer,
  bulkEditorOptions: {
    renderer: bulkNumberStepperEditor,
    defaultValue: 0,
  } as IBulkEditorOptions<number>,
  width: 150,
} as IDataGridColumnConfiguration;

const outsColumn = {
  dataKey: DataKey.Outs,
  columnName: ColumnName.Outs,
  cellRenderer: numberStepperRenderer,
  headerRenderer: simpleHeaderRenderer,
  bulkEditorOptions: {
    renderer: bulkNumberStepperEditor,
    defaultValue: 0,
  } as IBulkEditorOptions<number>,
  width: 150,
} as IDataGridColumnConfiguration;

const inningColumn = {
  dataKey: DataKey.Inning,
  columnName: ColumnName.Inning,
  cellRenderer: inningRenderer,
  headerRenderer: simpleHeaderRenderer,
  bulkEditorOptions: {
    renderer: bulkInningEditor,
    defaultValue: { inningNumber: 1, isTopInning: true } as IReviewInning,
  } as IBulkEditorOptions<IReviewInning>,
  width: 100,
} as IDataGridColumnConfiguration;

const pitcherDexterityColumn = {
  dataKey: DataKey.PitcherDexterity,
  columnName: ColumnName.PitcherDexterity,
  cellRenderer: (data: ICellRendererParams<string, ReviewDataRow>) => dropdownRenderer(data, enumToDropdownValuesMap(RightLeft)),
  headerRenderer: simpleHeaderRenderer,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, ReviewDataRow, void>) => bulkDropdownEditor(data, enumToDropdownValuesMap(RightLeft)),
    defaultValue: Dexterity.Undefined,
  },
  width: 110,
} as IDataGridColumnConfiguration;

const battingSideColumn = {
  dataKey: DataKey.BatterDexterity,
  columnName: ColumnName.BattingSide,
  cellRenderer: (data: ICellRendererParams<string, ReviewDataRow>) => dropdownRenderer(data, enumToDropdownValuesMap(RightLeft)),
  headerRenderer: simpleHeaderRenderer,
  width: 110,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, ReviewDataRow, void>) => bulkDropdownEditor(data, enumToDropdownValuesMap(RightLeft)),
    defaultValue: Dexterity.Undefined,
  },
} as IDataGridColumnConfiguration;

const taggedPitchColumn = {
  dataKey: DataKey.TaggedPitchType,
  columnName: ColumnName.TaggedPitch,
  cellRenderer: (data: ICellRendererParams<string, ReviewDataRow>) => dropdownRenderer(data, enumToDropdownValuesMap(PitchTypes)),
  headerRenderer: simpleHeaderRenderer,
  width: 170,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, ReviewDataRow, void>) => bulkDropdownEditor(data, enumToDropdownValuesMap(PitchTypes)),
    defaultValue: PitchTypes.Undefined,
  },
} as IDataGridColumnConfiguration;

const pitchCallColumn = {
  dataKey: DataKey.PitchCall,
  columnName: ColumnName.PitchCall,
  cellRenderer: (data: ICellRendererParams<string, ReviewDataRow>) =>
    dropdownRenderer(data, enumToDropdownValuesMap(CallTypes), '', '200px'),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, ReviewDataRow, void>) => bulkDropdownEditor(data, enumToDropdownValuesMap(CallTypes)),
    defaultValue: CallTypes.Undefined,
  },
} as IDataGridColumnConfiguration;

const kOrBBColumn = {
  dataKey: DataKey.KOrBB,
  columnName: ColumnName.KOrBB,
  cellRenderer: (data: ICellRendererParams<string, ReviewDataRow>) => dropdownRenderer(data, enumToDropdownValuesMap(KOrBB)),
  headerRenderer: simpleHeaderRenderer,
  width: 120,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, ReviewDataRow, void>) => bulkDropdownEditor(data, enumToDropdownValuesMap(KOrBB)),
    defaultValue: KOrBB.Undefined,
  },
} as IDataGridColumnConfiguration;

const hitTypeColumn = {
  dataKey: DataKey.HitType,
  columnName: ColumnName.HitType,
  cellRenderer: (data: ICellRendererParams<string, ReviewDataRow>) => dropdownRenderer(data, enumToDropdownValuesMap(HitTypes)),
  headerRenderer: simpleHeaderRenderer,
  width: 140,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, ReviewDataRow, void>) => bulkDropdownEditor(data, enumToDropdownValuesMap(HitTypes)),
    defaultValue: HitTypes.Undefined,
  },
} as IDataGridColumnConfiguration;

const playResultColumn = {
  dataKey: DataKey.PlayResult,
  columnName: ColumnName.PlayResult,
  cellRenderer: (data: ICellRendererParams<string, ReviewDataRow>) => dropdownRenderer(data, enumToDropdownValuesMap(ResultTypes)),
  headerRenderer: simpleHeaderRenderer,
  width: 140,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, ReviewDataRow, void>) => bulkDropdownEditor(data, enumToDropdownValuesMap(ResultTypes)),
    defaultValue: ResultTypes.Undefined,
  },
} as IDataGridColumnConfiguration;

const playResultColumnWithPlayerTracking = {
  dataKey: DataKey.PlayResult,
  columnName: ColumnName.PlayResult,
  cellRenderer: (data: ICellRendererParams<string, ReviewDataRow>) => dropdownRenderer(data, enumToDropdownValuesMap(AllResultTypes)),
  headerRenderer: simpleHeaderRenderer,
  width: 140,
  bulkEditorOptions: {
    renderer: (data: IBulkEditorParams<string, ReviewDataRow, void>) => bulkDropdownEditor(data, enumToDropdownValuesMap(AllResultTypes)),
    defaultValue: ResultTypes.Undefined,
  },
} as IDataGridColumnConfiguration;

const outsOnPlayColumn = {
  dataKey: DataKey.OutsOnPlay,
  columnName: ColumnName.OutsOnPlay,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) => numberStepperRenderer(data),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
  bulkEditorOptions: {
    renderer: bulkNumberStepperEditor,
    defaultValue: 0,
  } as IBulkEditorOptions<number>,
} as IDataGridColumnConfiguration;

const runsScoredColumn = {
  dataKey: DataKey.RunsScored,
  columnName: ColumnName.RunsScored,
  cellRenderer: numberStepperRenderer,
  headerRenderer: simpleHeaderRenderer,
  width: 150,
  bulkEditorOptions: {
    renderer: bulkNumberStepperEditor,
    defaultValue: 0,
  } as IBulkEditorOptions<number>,
} as IDataGridColumnConfiguration;

const absChallengeColumn = {
  dataKey: DataKey.AbsChallenge,
  columnName: ColumnName.AbsChallenge,
  cellRenderer: absChallengeRenderer,
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const trackStartTimeLocalColumn = {
  dataKey: DataKey.Time,
  columnName: ColumnName.Time,
  cellRenderer: timeCellRenderer,
  headerRenderer: simpleHeaderRenderer,
  width: 210,
} as IDataGridColumnConfiguration;

const releaseSpeedColumn = {
  dataKey: DataKey.ReleaseSpeed,
  columnName: ColumnName.ReleaseSpeed,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) => measurementRenderer(data, valueFormatters.mphFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const verticalReleaseAngleColumn = {
  dataKey: DataKey.VerticalReleaseAngle,
  columnName: ColumnName.VerticalReleaseAngle,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) =>
    measurementRenderer(data, valueFormatters.onePlaceDecimalValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const horizontalReleaseAngleColumn = {
  dataKey: DataKey.HorizontalReleaseAngle,
  columnName: ColumnName.HorizontalReleaseAngle,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) =>
    measurementRenderer(data, valueFormatters.onePlaceDecimalValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const spinRateColumn = {
  dataKey: DataKey.SpinRate,
  columnName: ColumnName.SpinRate,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) => measurementRenderer(data, valueFormatters.integerValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const spinAxisColumn = {
  dataKey: DataKey.SpinAxis,
  columnName: ColumnName.SpinAxis,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) => measurementRenderer(data, valueFormatters.integerValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const tiltColumn = {
  dataKey: DataKey.Tilt,
  columnName: ColumnName.Tilt,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) => measurementRenderer(data, valueFormatters.angleClockFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const releaseHeightColumn = {
  dataKey: DataKey.ReleaseHeight,
  columnName: ColumnName.ReleaseHeight,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) =>
    measurementRenderer(data, valueFormatters.feetAndInchesValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const releaseSideColumn = {
  dataKey: DataKey.ReleaseSide,
  columnName: ColumnName.ReleaseSide,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) =>
    measurementRenderer(data, valueFormatters.feetAndInchesValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const extensionColumn = {
  dataKey: DataKey.Extension,
  columnName: ColumnName.Extension,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) =>
    measurementRenderer(data, valueFormatters.feetAndInchesValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const verticalBreakColumn = {
  dataKey: DataKey.VerticalBreak,
  columnName: ColumnName.VerticalBreak,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) => measurementRenderer(data, valueFormatters.inchesValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const inducedVerticalBreakColumn = {
  dataKey: DataKey.InducedVerticalBreak,
  columnName: ColumnName.InducedVerticalBreak,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) => measurementRenderer(data, valueFormatters.inchesValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const horizontalBreakColumn = {
  dataKey: DataKey.HorizontalBreak,
  columnName: ColumnName.HorizontalBreak,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) => measurementRenderer(data, valueFormatters.inchesValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const plateLocHeightColumn = {
  dataKey: DataKey.PlateLocHeight,
  columnName: ColumnName.PlateLocHeight,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) =>
    measurementRenderer(data, valueFormatters.feetAndInchesValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const plateLocSideColumn = {
  dataKey: DataKey.PlateLocSide,
  columnName: ColumnName.PlateLocSide,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) =>
    measurementRenderer(data, valueFormatters.feetAndInchesValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const hitExitSpeedColumn = {
  dataKey: DataKey.HitExitSpeed,
  columnName: ColumnName.HitExitSpeed,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) => measurementRenderer(data, valueFormatters.mphFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const hitEstCarryColumn = {
  dataKey: DataKey.HitEstCarry,
  columnName: ColumnName.HitEstCarry,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) =>
    measurementRenderer(data, valueFormatters.feetAndInchesValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const hitDirectionColumn = {
  dataKey: DataKey.HitDirection,
  columnName: ColumnName.HitDirection,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) => measurementRenderer(data, valueFormatters.integerValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const hitBearingColumn = {
  dataKey: DataKey.HitBearing,
  columnName: ColumnName.HitBearing,
  cellRenderer: (data: ICellRendererParams<number, IReviewDataRow>) => measurementRenderer(data, valueFormatters.integerValueFormatter),
  headerRenderer: simpleHeaderRenderer,
  width: 150,
} as IDataGridColumnConfiguration;

const firstBaseColumn = {
  dataKey: DataKey.FirstBase,
  columnName: ColumnName.RunnerOnFirst,
  // TODO: move renderers to BSC
  cellRenderer: baseRunnerRenderer,
  bulkEditorOptions: {
    renderer: bulkPlayerEditor,
  },
  headerRenderer: simpleHeaderRenderer,
  width: 200,
} as IDataGridColumnConfiguration;

const secondBaseColumn = {
  dataKey: DataKey.SecondBase,
  columnName: ColumnName.RunnerOnSecond,
  // TODO: move renderers to BSC
  cellRenderer: baseRunnerRenderer,
  bulkEditorOptions: {
    renderer: bulkPlayerEditor,
  },
  headerRenderer: simpleHeaderRenderer,
  width: 200,
} as IDataGridColumnConfiguration;

const thirdBaseColumn = {
  dataKey: DataKey.ThirdBase,
  columnName: ColumnName.RunnerOnThird,
  // TODO: move renderers to BSC
  cellRenderer: baseRunnerRenderer,
  bulkEditorOptions: {
    renderer: bulkPlayerEditor,
  },
  headerRenderer: simpleHeaderRenderer,
  width: 200,
} as IDataGridColumnConfiguration;

const firstBaseman = {
  dataKey: DataKey.FirstBaseman,
  columnName: ColumnName.FirstBaseman,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: bulkPlayerEditor,
  },
  headerRenderer: simpleHeaderRenderer,
  width: 200,
} as IDataGridColumnConfiguration;

const secondBaseman = {
  dataKey: DataKey.SecondBaseman,
  columnName: ColumnName.SecondBaseman,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: bulkPlayerEditor,
  },
  headerRenderer: simpleHeaderRenderer,
  width: 200,
} as IDataGridColumnConfiguration;

const thirdBaseman = {
  dataKey: DataKey.ThirdBaseman,
  columnName: ColumnName.ThirdBaseman,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: bulkPlayerEditor,
  },
  headerRenderer: simpleHeaderRenderer,
  width: 200,
} as IDataGridColumnConfiguration;

const shortStopper = {
  dataKey: DataKey.ShortStopper,
  columnName: ColumnName.ShortStopper,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: bulkPlayerEditor,
  },
  headerRenderer: simpleHeaderRenderer,
  width: 200,
} as IDataGridColumnConfiguration;

const leftFielder = {
  dataKey: DataKey.LeftFielder,
  columnName: ColumnName.LeftFielder,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: bulkPlayerEditor,
  },
  headerRenderer: simpleHeaderRenderer,
  width: 200,
} as IDataGridColumnConfiguration;

const centerFielder = {
  dataKey: DataKey.CenterFielder,
  columnName: ColumnName.CenterFielder,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: bulkPlayerEditor,
  },
  headerRenderer: simpleHeaderRenderer,
  width: 200,
} as IDataGridColumnConfiguration;

const rightFielder = {
  dataKey: DataKey.RightFielder,
  columnName: ColumnName.RightFielder,
  // TODO: move renderers to BSC
  cellRenderer: defensivePlayerRenderer,
  bulkEditorOptions: {
    renderer: bulkPlayerEditor,
  },
  headerRenderer: simpleHeaderRenderer,
  width: 200,
} as IDataGridColumnConfiguration;

export const SessionReviewColumnConfigurations = [
  noteColumn,
  trackStartTimeLocalColumn,
  pitchNoColumn,
  pitcherColumn,
  pitcherDexterityColumn,
  catcherColumn,
  batterColumn,
  battingSideColumn,
  ballsColumn,
  strikesColumn,
  outsColumn,
  inningColumn,
  taggedPitchColumn,
  pitchCallColumn,
  kOrBBColumn,
  hitTypeColumn,
  playResultColumn,
  outsOnPlayColumn,
  runsScoredColumn,
  absChallengeColumn,
  releaseSpeedColumn,
  verticalReleaseAngleColumn,
  horizontalReleaseAngleColumn,
  spinRateColumn,
  spinAxisColumn,
  tiltColumn,
  releaseHeightColumn,
  releaseSideColumn,
  extensionColumn,
  verticalBreakColumn,
  inducedVerticalBreakColumn,
  horizontalBreakColumn,
  plateLocHeightColumn,
  plateLocSideColumn,
  hitExitSpeedColumn,
  hitEstCarryColumn,
  hitDirectionColumn,
  hitBearingColumn,
];

export const SessionReviewColumnConfigurationsWithPlayerTracking = [
  noteColumn,
  trackStartTimeLocalColumn,
  pitchNoColumn,
  pitcherColumn,
  pitcherDexterityColumn,
  catcherColumn,
  batterColumn,
  battingSideColumn,
  ballsColumn,
  strikesColumn,
  outsColumn,
  inningColumn,
  taggedPitchColumn,
  pitchCallColumn,
  kOrBBColumn,
  hitTypeColumn,
  playResultColumnWithPlayerTracking,
  outsOnPlayColumn,
  runsScoredColumn,
  absChallengeColumn,
  releaseSpeedColumn,
  verticalReleaseAngleColumn,
  horizontalReleaseAngleColumn,
  spinRateColumn,
  spinAxisColumn,
  tiltColumn,
  releaseHeightColumn,
  releaseSideColumn,
  extensionColumn,
  verticalBreakColumn,
  inducedVerticalBreakColumn,
  horizontalBreakColumn,
  plateLocHeightColumn,
  plateLocSideColumn,
  hitExitSpeedColumn,
  hitEstCarryColumn,
  hitDirectionColumn,
  hitBearingColumn,
];

export const PlayerTrackingColumnConfigurations = [
  firstBaseman,
  secondBaseman,
  thirdBaseman,
  shortStopper,
  leftFielder,
  centerFielder,
  rightFielder,
  firstBaseColumn,
  secondBaseColumn,
  thirdBaseColumn,
];

export const BpColumnConfigurations = [
  noteColumn,
  trackStartTimeLocalColumn,
  pitchNoColumn,
  batterColumn,
  battingSideColumn,
  hitTypeColumn,
  releaseSpeedColumn,
  verticalReleaseAngleColumn,
  horizontalReleaseAngleColumn,
  spinRateColumn,
  spinAxisColumn,
  tiltColumn,
  releaseHeightColumn,
  releaseSideColumn,
  extensionColumn,
  verticalBreakColumn,
  inducedVerticalBreakColumn,
  horizontalBreakColumn,
  plateLocHeightColumn,
  plateLocSideColumn,
  hitExitSpeedColumn,
  hitEstCarryColumn,
  hitDirectionColumn,
  hitBearingColumn,
];

export const getColumn = (dataKey: string): IDataGridColumnConfiguration | undefined => {
  return SessionReviewColumnConfigurations.find((x) => x.dataKey === dataKey);
};
